/**
 * Access governance — Zod schemas for the unified queue
 * (recovery + access requests in one surface).
 */

import { z } from "zod";

export const requestKindSchema = z.enum(["recovery", "access"]);
export type RequestKind = z.infer<typeof requestKindSchema>;

export const decisionKindSchema = z.enum([
  "approved",
  "denied",
  "needs_info",
  "reversed",
]);
export type DecisionKind = z.infer<typeof decisionKindSchema>;

export const attestationMethodSchema = z.enum([
  "video_call",
  "in_person",
  "phone_call",
  "government_id",
  "manager_attestation",
  "security_question",
  "other",
]);
export type AttestationMethod = z.infer<typeof attestationMethodSchema>;

export { queueEntrySchema, queueResponseSchema } from "@/lib/governance-contract";
export type QueueEntry = z.infer<typeof import("@/lib/governance-contract").queueEntrySchema>;
export type QueueResponse = z.infer<typeof import("@/lib/governance-contract").queueResponseSchema>;

export const commentSourceSchema = z.enum([
  "chat",
  "decision",
  // "needs_info" is the body of a peer-decision with verdict
  // needs_info, surfaced as a conversation row so the admin
  // sees their own Request-info text in the Discussion panel
  // they typed it into. Distinct from "decision" (approve/deny
  // mirrors) because the admin Discussion projection drops
  // those but passes through needs_info — see backend's
  // filterAdminDiscussionComments docblock.
  "needs_info",
  "lifecycle",
]);
export type CommentSource = z.infer<typeof commentSourceSchema>;

export const threadCommentSchema = z.object({
  ID: z.string().uuid(),
  RequestKind: requestKindSchema,
  RequestID: z.string().uuid(),
  AuthorKind: z.enum(["user", "admin", "system"]),
  AuthorUserID: z.string().uuid().nullable().optional(),
  AuthorAdminID: z.string().uuid().nullable().optional(),
  AuthorEmail: z.string().optional().default(""),
  AuthorDisplayName: z.string().optional().default(""),
  Body: z.string(),
  EvidenceIDs: z.array(z.string().uuid()).optional().default([]),
  /** Origin discriminator. "chat" = real back-and-forth post.
   *  "decision" = server-mirrored approve/deny verdict body
   *  (admin Discussion suppresses these; portal shows them with
   *  a "Decision" tag). "needs_info" = server-mirrored Request-
   *  info question body — rendered as a chat-shape row with a
   *  small "Asked for info" affix so the admin sees their own
   *  question in the conversation surface they typed it into.
   *  "lifecycle" = server-posted state-change marker (revoke /
   *  cancel / expire). Defaults to "chat" for legacy rows the
   *  backend hasn't yet stamped. */
  Source: commentSourceSchema.optional().default("chat"),
  CreatedAt: z.string(),
});
export type ThreadComment = z.infer<typeof threadCommentSchema>;

export const decisionRowSchema = z.object({
  ID: z.string().uuid(),
  RequestKind: requestKindSchema,
  RequestID: z.string().uuid(),
  ApproverID: z.string().uuid(),
  ApproverEmail: z.string().optional().default(""),
  ApproverDisplayName: z.string().optional().default(""),
  Decision: decisionKindSchema,
  ReversesID: z.string().uuid().nullable().optional(),
  Comment: z.string(),
  DecidedAt: z.string(),
  // IsPlatformOverride is TRUE when this row was written by the
  // break-glass platform-override flow (a platform admin force-
  // deciding a stuck request). The substrate aggregator skips
  // override rows when counting toward N-of-M, but they remain
  // visible in the decision history with a distinct badge so
  // an auditor never confuses a force-decide with peer consensus.
  IsPlatformOverride: z.boolean(),
});
export type DecisionRow = z.infer<typeof decisionRowSchema>;

export const attestationRowSchema = z.object({
  ID: z.string().uuid(),
  RequestKind: requestKindSchema,
  RequestID: z.string().uuid(),
  Method: attestationMethodSchema,
  EvidenceID: z.string().uuid().nullable().optional(),
  Notes: z.string(),
  AttestedBy: z.string().uuid(),
  AttestedByEmail: z.string().optional().default(""),
  AttestedAt: z.string(),
});
export type AttestationRow = z.infer<typeof attestationRowSchema>;

// JIT subsystem health snapshot — wire shape from
// GET /access-governance/health. Drives the small status card
// at the top of the queue page so operators see at-a-glance
// whether the worker substrate is healthy. Sweep timestamps
// are process-local (single-replica today), captured per-kind
// by MarkSweepCompleted in the backend.
export const jitHealthSchema = z.object({
  tenant_id: z.string().uuid(),
  queue_depth_pending: z.number().int(),
  queue_depth_awaiting_info: z.number().int(),
  oldest_pending_at: z.string().nullable().optional(),
  active_grants: z.number().int(),
  expiring_soon_count: z.number().int(),
  last_expiry_sweep_at: z.string().nullable().optional(),
  last_reminder_sweep_at: z.string().nullable().optional(),
  last_stale_pending_sweep_at: z.string().nullable().optional(),
  last_unavailable_sweep_at: z.string().nullable().optional(),
  last_deactivation_sweep_at: z.string().nullable().optional(),
  health_status: z.enum(["green", "amber", "red"]),
});
export type JITHealth = z.infer<typeof jitHealthSchema>;

export const queueFilterSchema = z.object({
  kind: requestKindSchema.optional(),
  priority: z.enum(["critical", "high", "normal"]).optional(),
  // "all" means show every status. Anything else (or unset) shows only
  // open requests (pending + awaiting_info) — the backend's default.
  status: z.enum(["all"]).optional(),
  // SLA triage filter. The UI exposes two: "breaching" (at/over SLA — overdue OR
  // escalates within 24h) and "overdue" (the urgent subset past its escalation
  // point, which the access escalation worker has/will widen to the admin pool).
  // The "show me what's at/over SLA" control that turns the queue into governance.
  // "due_today" is also accepted (the backend's third value, and any stale link):
  // the toolbar folds it into "breaching". `.catch(undefined)` so an unrecognized
  // sla degrades to the unfiltered queue instead of throwing the route boundary.
  sla: z.enum(["breaching", "overdue", "due_today"]).optional().catch(undefined),
  page: z.coerce.number().int().min(1).optional(),
  // Currently-focused request id (drives the master-detail right pane).
  selected: z.string().uuid().optional(),
});
export type QueueFilter = z.infer<typeof queueFilterSchema>;

// Terminal (decided/closed) statuses — the History view's universe. A request
// leaves the live queue into exactly one of these.
export const decidedStatusSchema = z.enum(["approved", "denied", "expired", "cancelled"]);
export type DecidedStatus = z.infer<typeof decidedStatusSchema>;

// historyFilterSchema drives the read-only Identity Governance History view
// (decided/closed requests, date-range + export). URL-driven like every other
// list/filter state. `from`/`to` are yyyy-mm-dd (the native date input value);
// the query layer expands them to an inclusive RFC3339 day range for the backend.
export const historyFilterSchema = z.object({
  kind: requestKindSchema.optional(),
  // Unset = all decided statuses; set = pin one terminal status.
  status: decidedStatusSchema.optional(),
  from: z.string().optional(),
  to: z.string().optional(),
  page: z.coerce.number().int().min(1).optional(),
});
export type HistoryFilter = z.infer<typeof historyFilterSchema>;
